import axios from 'axios';
import { configureAuthInterceptors } from './authConfig';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout for content operations
});

// Configure auth interceptors (credentials, group header, 401 handling)
configureAuthInterceptors(api);

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('Content API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Content API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('Content API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('Content API Response Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export const contentApi = {
  // =========================================================================
  // Folder Operations
  // =========================================================================

  // List root folders or children of a folder
  listFolders: async (parentPath = null, contentType = null, signal = null) => {
    const params = {};
    if (parentPath) params.parent_path = parentPath;
    if (contentType) params.content_type = contentType;

    const response = await api.get('/content/folders', { params, signal });
    // API returns {status, folders} - extract folders array
    return response.data.folders || [];
  },

  // Get a specific folder
  getFolder: async (path, signal = null) => {
    const response = await api.get(`/content/folders/${encodeURIComponent(path)}`, { signal });
    // API returns {status, folder}
    return response.data.folder;
  },

  // Create a new folder
  createFolder: async (path, contentType = null, description = null) => {
    const response = await api.post('/content/folders', {
      path,
      content_type: contentType,
      description,
    });
    // API returns {status, folder}
    return response.data.folder;
  },

  // =========================================================================
  // Content Item Operations
  // =========================================================================

  // List content items with optional filters
  listItems: async (options = {}, signal = null) => {
    const params = {};
    if (options.contentType) params.content_type = options.contentType;
    if (options.folderPath) params.folder_path = options.folderPath;
    if (options.tags) params.tags = options.tags.join(',');
    if (options.limit) params.limit = options.limit;
    if (options.offset) params.offset = options.offset;

    const response = await api.get('/content/items', { params, signal });
    // API returns {status, items, count} - extract items array
    return response.data.items || [];
  },

  // Get a specific content item
  getItem: async (itemId, includeVersions = false, signal = null) => {
    const params = {};
    if (includeVersions) params.include_versions = true;

    const response = await api.get(`/content/items/${itemId}`, { params, signal });
    // API returns {status, item}
    return response.data.item;
  },

  // Create a new content item
  // Content is base64 encoded to avoid Vercel WAF blocking YAML multiline patterns
  createItem: async (data) => {
    // Base64 encode content if present and is a string (not already encoded)
    const payload = { ...data };
    if (payload.content && typeof payload.content === 'string' && !payload._skipEncode) {
      payload.content = btoa(unescape(encodeURIComponent(payload.content)));
    }
    delete payload._skipEncode;

    const response = await api.post('/content/items', payload);
    // API returns {status, item}
    return response.data.item;
  },

  // Get content (raw bytes) for an item
  // If published=true, fetches the published version (falls back to latest if none published)
  getContent: async (itemId, versionNumber = null, signal = null, published = false) => {
    const params = {};
    if (versionNumber) params.version = versionNumber;
    if (published) params.published = true;

    const response = await api.get(`/content/items/${itemId}/content`, {
      params,
      signal,
      responseType: 'text',
    });
    return response.data;
  },

  // =========================================================================
  // Version Operations
  // =========================================================================

  // Save a new draft
  // Content is base64 encoded to avoid Vercel WAF blocking YAML multiline patterns
  saveDraft: async (itemId, content, commitMessage = null) => {
    // Base64 encode the content - required by backend and avoids WAF issues
    const encodedContent = typeof content === 'string' ? btoa(unescape(encodeURIComponent(content))) : content;

    const response = await api.post(`/content/items/${itemId}/drafts`, {
      content: encodedContent,
      commit_message: commitMessage,
    });
    // API returns {status, version}
    return response.data.version;
  },

  // Publish a version
  publishVersion: async (itemId, versionId = null) => {
    const data = {};
    if (versionId) data.version_id = versionId;

    const response = await api.post(`/content/items/${itemId}/publish`, data);
    // API returns {status, version}
    return response.data.version;
  },

  // List all versions of an item
  listVersions: async (itemId, limit = 50, signal = null) => {
    const params = { limit };
    const response = await api.get(`/content/items/${itemId}/versions`, { params, signal });
    // API returns {status, versions}
    return response.data.versions || [];
  },

  // Get a specific version
  getVersion: async (itemId, versionNumber, signal = null) => {
    const response = await api.get(`/content/items/${itemId}/versions/${versionNumber}`, { signal });
    // API returns {status, version}
    return response.data.version;
  },

  // Get content for a specific version
  getVersionContent: async (itemId, versionNumber, signal = null) => {
    const response = await api.get(`/content/items/${itemId}/content`, {
      params: { version: versionNumber },
      signal,
      responseType: 'text',
    });
    return response.data;
  },

  // =========================================================================
  // Validation
  // =========================================================================

  // Validate content against schema
  // Content is base64 encoded to avoid Vercel WAF blocking YAML multiline patterns
  validateContent: async (itemId, content) => {
    const encodedContent = typeof content === 'string' ? btoa(unescape(encodeURIComponent(content))) : content;
    const response = await api.post(`/content/items/${itemId}/validate`, { content: encodedContent });
    // API returns {status, errors}
    return response.data.errors || [];
  },

  // =========================================================================
  // Dependencies
  // =========================================================================

  // Get dependencies for an item
  getDependencies: async (itemId, versionNumber = null, signal = null) => {
    const params = {};
    if (versionNumber) params.version = versionNumber;

    const response = await api.get(`/content/items/${itemId}/dependencies`, { params, signal });
    // API returns {status, dependencies}
    return response.data.dependencies || [];
  },

  // Get items that depend on this item
  getDependents: async (itemId, signal = null) => {
    const response = await api.get(`/content/items/${itemId}/dependents`, { signal });
    // API returns {status, dependents}
    return response.data.dependents || [];
  },

  // =========================================================================
  // Clone
  // =========================================================================

  // Clone a content item (full copy)
  cloneItem: async (itemId, options = {}) => {
    const data = {};
    if (options.name) data.name = options.name;
    if (options.folderPath) data.folder_path = options.folderPath;
    if (options.ownerId) data.owner_id = options.ownerId;
    if (options.groupId) data.group_id = options.groupId;

    const response = await api.post(`/content/items/${itemId}/clone`, data);
    // API returns {status, item}
    return response.data.item;
  },
};

export default contentApi;
